import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Clock, Crown } from 'lucide-react';
import api from '../../lib/api';

const RANK_COLORS = {
  Bronze: '#B08B58', Silver: '#C0C0C0', Gold: '#E9C46A',
  Platinum: '#8ECAE6', Diamond: '#FF6EC7',
  Master: '#A78BFA', Grandmaster: '#F43F5E',
};

export default function SeasonsPage() {
  const nav = useNavigate();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [me, setMe] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/arena/seasons/current').then(r => r.data?.season).catch(() => null),
      api.get('/arena/seasons/history?limit=12').then(r => r.data?.seasons || []).catch(() => []),
      api.get('/arena/seasons/me').then(r => r.data).catch(() => null),
    ]).then(([cur, hist, m]) => {
      setCurrent(cur);
      setHistory(hist);
      setMe(m);
      setSelectedSeason(cur?.season_id || (hist[0]?.season_id));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedSeason) return;
    api.get(`/arena/seasons/leaderboard?season_id=${encodeURIComponent(selectedSeason)}&limit=50`)
      .then(r => setLeaderboard(r.data?.leaderboard || []))
      .catch(() => setLeaderboard([]));
  }, [selectedSeason]);

  if (loading) return <div className="p-8 text-center text-muted-foreground text-xs">Loading seasons…</div>;

  const daysLeft = (() => {
    if (!current) return null;
    try {
      const end = new Date(current.ends_at);
      const now = new Date();
      const diff = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(0, diff);
    } catch { return null; }
  })();

  return (
    <div className="pb-24 space-y-5" data-testid="seasons-page">
      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={() => nav(-1)}
          data-testid="seasons-back-btn"
          className="p-2 rounded-full hover:bg-white/5"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Ranked</p>
          <h1 className="font-serif text-2xl">Season Leaderboards</h1>
        </div>
      </div>

      {current && (
        <div className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-black/50 p-4" data-testid="current-season">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-amber-300/70">Current Season</p>
              <p className="font-serif text-lg">{current.name}</p>
              <p className="text-[11px] text-muted-foreground">{current.season_id}</p>
            </div>
            {daysLeft !== null && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" /> Time left
                </p>
                <p className="font-mono text-lg text-amber-300">{daysLeft}d</p>
              </div>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg bg-black/30 p-2">
              <p className="text-muted-foreground text-[10px]">Your rank</p>
              <p className="font-mono" style={{ color: RANK_COLORS[me?.stats?.tier] || '#E9C46A' }}>
                {me?.stats?.tier || 'Unranked'}
              </p>
            </div>
            <div className="rounded-lg bg-black/30 p-2">
              <p className="text-muted-foreground text-[10px]">Your rating</p>
              <p className="font-mono text-white">{me?.stats?.rating ?? '—'}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="season-picker">
        {history.map((s) => (
          <button
            key={s.season_id}
            onClick={() => setSelectedSeason(s.season_id)}
            data-testid={`season-tab-${s.season_id}`}
            className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest transition ${
              selectedSeason === s.season_id
                ? 'bg-amber-500 text-black border border-amber-300'
                : 'bg-black/40 text-muted-foreground border border-white/10 hover:border-white/25'
            }`}
          >
            {s.season_id}{s.active && ' •'}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-white/5 bg-black/30 overflow-hidden" data-testid="leaderboard-list">
        <div className="px-3 py-2 flex items-center gap-2 border-b border-white/5">
          <Trophy className="w-3.5 h-3.5 text-amber-300" />
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            Top explorers · {selectedSeason || '—'}
          </span>
        </div>
        {leaderboard.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground" data-testid="leaderboard-empty">
            No ranked matches recorded yet.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {leaderboard.map((row, i) => (
              <motion.div
                key={row.user_id}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.02 }}
                className="flex items-center gap-3 px-3 py-2"
                data-testid={`leaderboard-row-${i}`}
              >
                <span className={`w-6 text-center font-mono text-xs ${i < 3 ? 'text-amber-300' : 'text-muted-foreground'}`}>
                  {i === 0 ? <Crown className="w-4 h-4 inline text-amber-400" /> : `#${i + 1}`}
                </span>
                {row.picture ? (
                  <img src={row.picture} alt="" className="w-7 h-7 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-white/5 border border-white/10" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    {row.display_name}
                    {row.is_bot && <span className="ml-1 text-[9px] text-muted-foreground">· AI</span>}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {row.wins || 0}W / {row.losses || 0}L{row.draws ? ` / ${row.draws}D` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-sm text-white">{row.rating}</p>
                  <p className="text-[9px] uppercase tracking-widest" style={{ color: RANK_COLORS[row.tier] || '#94A3B8' }}>
                    {row.tier}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
